import type { MutationTree } from 'vuex';
import moment from 'moment';
import type { DocumentAttachment } from '@getaccept/lib-shared/src/types/document-attachment';
import type { RecipientStatus } from '@getaccept/lib-shared/src/recipients/types/enums/recipient-status';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { DocumentRecipientRevision } from '@getaccept/lib-shared/src/types/document-revision';
import type { LoadImagePayload } from '@getaccept/editor-lib';
import { setImageSrcURL } from '@getaccept/editor-lib';
import type { SigningPageDocument } from '@getaccept/lib-shared/src/types/signing-page-document';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import type { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import type { DocumentState } from './document.types';
import { DocumentMutation } from './document.constants';
export const mutations: MutationTree<DocumentState> = {
  [DocumentMutation.SetLoading](state: DocumentState, loading: boolean) {
    state.isLoading = loading;
  },
  [DocumentMutation.SetEditorRecipientInputLoading](state: DocumentState, loading: boolean) {
    state.isEditorRecipientInputLoading = loading;
  },
  [DocumentMutation.SetRevisionsLoading](state: DocumentState, loading: boolean) {
    state.revisionsIsLoading = loading;
  },
  [DocumentMutation.SetRevisionsViewedLoading](state: DocumentState, loading: boolean) {
    state.revisionsViewedLoading = loading;
  },
  [DocumentMutation.SetEditorRecipientInput](
    state: DocumentState,
    editorRecipientInput: EditorRecipientInput[]
  ) {
    state.editorRecipientInput = editorRecipientInput;
  },
  [DocumentMutation.SetButtonLoading](state: DocumentState, loading: boolean) {
    state.isButtonLoading = loading;
  },
  [DocumentMutation.SetForwarded](state: DocumentState, forwarded: boolean) {
    state.forwarded = forwarded;
  },
  [DocumentMutation.SetDocument](state: DocumentState, document: SigningPageDocument) {
    state.document = document;
  },
  [DocumentMutation.SetImageURL](
    state: DocumentState,
    payload: LoadImagePayload & { srcUrl: string; externalId: string }
  ) {
    state.document = setImageSrcURL(state.document, payload) as SigningPageDocument;
  },
  [DocumentMutation.SetDocumentRevisions](
    state: DocumentState,
    documentRevisions: DocumentRecipientRevision[]
  ) {
    state.document.documentRevisions = documentRevisions;
  },
  [DocumentMutation.SetPdfUrl](state: DocumentState, pdfUrl: string) {
    state.pdfUrl = pdfUrl;
  },
  [DocumentMutation.SetFocusedRequiredId](state: DocumentState, focusedRequiredId: string) {
    state.focusedRequiredId = focusedRequiredId;
  },
  [DocumentMutation.SetDocumentStatus](state: DocumentState, status: DocumentStatus) {
    state.document.status = status;
  },
  [DocumentMutation.SetDocumentSignedDate](state: DocumentState, date: string) {
    state.document.signDate = date;
  },
  [DocumentMutation.UpdateRecipients](state: DocumentState, recipients: SigningPageRecipient[]) {
    state.document.recipients = recipients;
  },
  [DocumentMutation.SetRecipientStatus](
    state: DocumentState,
    { recipientId, status }: { recipientId: string; status: RecipientStatus }
  ) {
    const recipientsUpdated: SigningPageRecipient[] = state.document.recipients.map(recipient =>
      recipient.id === recipientId ? { ...recipient, status } : recipient
    );
    state.document = { ...state.document, recipients: recipientsUpdated };
  },
  [DocumentMutation.SetDocumentAttachments](
    state: DocumentState,
    attachments: DocumentAttachment[]
  ) {
    state.document.documentAttachments = attachments;
  },
  [DocumentMutation.SetAttachmentView](state: DocumentState, id: string) {
    const attachments: DocumentAttachment[] = [...state.document.documentAttachments];
    const index: number = attachments.findIndex((item: DocumentAttachment) => item.id === id);
    attachments[index].lastView = moment().toISOString();
    state.document.documentAttachments = attachments;
  },
  [DocumentMutation.SetError](state: DocumentState, error: boolean) {
    state.error = error;
  },
  [DocumentMutation.SetIsDownloading](state: DocumentState, isDownloading: boolean) {
    state.isDownloading = isDownloading;
  },
  [DocumentMutation.SetPartiallySignedFields](
    state: DocumentState,
    partiallySignedFields: PartiallySignedEvent[]
  ) {
    state.partiallySignedFields = partiallySignedFields;
  },
  [DocumentMutation.SetIsProcessing](state: DocumentState, isProcessing: boolean) {
    state.isProcessing = isProcessing;
  },
};
